import Text from "../../../Text/index.tsx";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog.tsx";
import { useEffect, type FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form.tsx";
import { Button } from "../../../ui/button.tsx";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { triggerEvent } from "@/lib/utils.ts";
import { useTranslation } from "react-i18next";

const items = [
  {
    id: "analyse",
    label: "analyse_competition",
  },
  {
    id: "compare",
    label: "compare_different_stores",
  },
  {
    id: "ia",
    label: "use_ai_for_benchmarking",
  },
  {
    id: "understand",
    label: "understand_the_market",
  },
  {
    id: "no_reason",
    label: "no_reason_yet",
  },
];

const formSchema = z.object({
  items: z.array(z.string()).refine((value) => value.some((item) => item), {
    message: "Vous devez sélectionné au moins une raison",
  }),
});

type Props = {
  onChangeValue: (name: string, value: string | string[]) => void;
  isLoading: boolean;
};

const StepThree: FC<Props> = ({ onChangeValue, isLoading }) => {
  const { t } = useTranslation();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      items: [],
    },
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onChangeValue("businessObjectives", values.items);
  };

  useEffect(() => {
    triggerEvent("Onboarding Step Viewed", {
      step_name: "Step Three - Business Objectives",
      step_index: 3,
    });

    return () => {
      triggerEvent("Onboarding Step Completed", {
        step_name: "Step Three - Business Objectives",
        step_index: 3,
      });
    };
  }, []);

  return (
    <DialogContent showCloseButton={false}>
      <VisuallyHidden>
        <DialogTitle>Onboarding</DialogTitle>
      </VisuallyHidden>
      <DialogHeader>
        <VisuallyHidden>
          <DialogDescription>Onboarding, what's your job</DialogDescription>
        </VisuallyHidden>
        <img
          src="/svg/krunsh-logo.svg"
          alt="krunsh logo"
          height={36}
          width={120}
        />
        <div className="flex flex-col gap-[4px]">
          <Text fontWeight={600} fontSize={20} color="#44546F">
            {t("start_with_krunsh")}
          </Text>
          <Text fontWeight={400} fontSize={14} color="#44546F">
            {t("start_with_onboarding")}
          </Text>
        </div>

        <div className="flex flex-col mb-[24px] mt-[36px]">
          <Text fontSize={12} fontWeight={400}>
            {t("onboarding_step_1_title")}
          </Text>
          <Text fontSize={20} fontWeight={600}>
            {t("onboarding_step_3_description")}
          </Text>
        </div>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="items"
            render={() => (
              <FormItem>
                {items.map((item) => (
                  <FormField
                    key={item.id}
                    control={form.control}
                    name="items"
                    render={({ field }) => {
                      return (
                        <FormItem
                          key={item.id}
                          className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]"
                        >
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(item.id)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...field.value, item.id])
                                  : field.onChange(
                                      field.value?.filter(
                                        (value) => value !== item.id,
                                      ),
                                    );
                              }}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal">
                            <Text fontSize={14} fontWeight={400}>
                              {t(item.label)}
                            </Text>
                          </FormLabel>
                        </FormItem>
                      );
                    }}
                  />
                ))}
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            size="lg"
            type="submit"
            variant="primaryFilled"
            className="w-full mt-[24px]"
            disabled={!form.formState.isValid || isLoading}
          >
            {t("start")}
          </Button>
        </form>
      </Form>
    </DialogContent>
  );
};

export default StepThree;
